import React, { useEffect, useState } from 'react';

import { Button } from 'antd';

import addMonths from 'date-fns/addMonths';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import type { FormikProps } from 'formik';
import { Field, Form as FormikForm } from 'formik';
import { formatDate } from 'helpers/date';
import { FilterPanel } from 'v2/components/Pages/Reports/FilterPanel';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import TextFormField from 'v2/components/Shared/FormFields/TextFormField';
import { parseDdMmYyyy } from 'v2/formatters/date';
import { sendEventToGA } from 'v2/helpers/analytics';
import { useMutableOptions } from 'v2/hooks/useMutableOptions';
import type { EFinancialStatementTabs, FinancialStatementFilter } from 'v2/interfaces/financial';
import { MovementsType, SearchType, TransactionsType } from 'v2/interfaces/financial';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import { Icon } from '@eduzz/ui-antd-theme';

import { MovementTypesFilter, SearchTypeFilter, TransactionTypesFilter, calendarPreset } from './constants';

interface IFormProps extends FormikProps<FinancialStatementFilter> {
  currentTab: EFinancialStatementTabs;
}

const translateFieldName = (field: string) => {
  if (field === 'periodPreset' || field === 'dates') {
    return 'Últimos Lançamentos';
  }

  if (field === 'movement_type') {
    return 'Movimentações';
  }

  if (field === 'transaction_type') {
    return 'Tipo da Transação';
  }

  return 'Busque por';
};

function Form({ initialValues, values, currentTab, setFieldValue, errors }: IFormProps) {
  // TODO: migrate to react-hook-form
  const [oldValues, setOldValues] = useState<FinancialStatementFilter>(initialValues);
  const [isSearchSent, setIsSearchSent] = useState<boolean>(false);

  const isStatementTab = currentTab === 'statement';

  const transactionTypesFilter = useMutableOptions({
    options: TransactionTypesFilter,
    callback: option => values.movement_type === MovementsType.ALL || option.type.includes(values.movement_type)
  });

  const transactionEqualMovementType = transactionTypesFilter.filter(
    transaction => transaction.value === values.transaction_type
  );

  const handleSearch = () => {
    financialStatementService.setFilter(values);
    setIsSearchSent(true);
  };

  const PlaceholderForSearchType = (searchType: SearchType) => {
    const SearchTypeSelected = SearchTypeFilter.find(searchTypeValue => searchTypeValue.value === searchType);
    return SearchTypeSelected?.placeholder;
  };

  useEffect(() => {
    if (isStatementTab) {
      if (transactionEqualMovementType.length === 0) {
        setFieldValue('transaction_type', TransactionsType.ALL);
      }

      if (values.dates?.[0] && values.dates?.[1]) {
        const start = startOfDay(parseDdMmYyyy(values.dates[0]));
        const end = startOfDay(parseDdMmYyyy(values.dates[1]));
        const today = startOfDay(new Date());
        const isOrdered = !isBefore(end, start);
        const isNotFuture = !isAfter(start, today) && !isAfter(end, today);
        const isWithinMaxRange = !isAfter(end, startOfDay(addMonths(start, 6)));

        if (isOrdered && isNotFuture && isWithinMaxRange) {
          financialStatementService.setFilter({
            periodPreset: values.periodPreset,
            dates: values.dates,
            movement_type: values.movement_type,
            transaction_type: values.transaction_type
          });
        }
      }

      setOldValues(values);

      Object.keys(values).forEach((field, index) => {
        const oldValue = values[field as keyof FinancialStatementFilter];
        const newValue = Object.entries(oldValues)[index][1];

        if (oldValue !== newValue) {
          sendEventToGA({
            category: 'Novo Extrato',
            action: 'Filtro',
            label: translateFieldName(field)
          });
        }
      });
    }
  }, [values, isStatementTab, setFieldValue, oldValues, transactionEqualMovementType.length]);

  useEffect(() => {
    if (values.search_text === '' && isSearchSent) {
      financialStatementService.setFilter({
        ...values,
        search_type: SearchType.ALL
      });
      setIsSearchSent(false);
    }
  }, [isSearchSent, values, values.search_text]);

  useEffect(() => {
    if (values.search_type === SearchType.ALL && !errors.dates) {
      setFieldValue('search_text', '');
    }
  }, [setFieldValue, values.search_type, errors.dates]);

  useEffect(() => {
    if (isStatementTab) {
      return;
    }

    const resetDates = [formatDate(subDays(new Date(), 6), 'dd/MM/yyyy'), formatDate(new Date(), 'dd/MM/yyyy')];

    setFieldValue('movement_type', MovementsType.ALL);
    setFieldValue('transaction_type', TransactionsType.ALL);
    setFieldValue('periodPreset', '7');
    setFieldValue('dates', resetDates);

    financialStatementService.setFilter({
      movement_type: MovementsType.ALL,
      transaction_type: TransactionsType.ALL,
      periodPreset: '7',
      dates: resetDates
    });
  }, [isStatementTab, setFieldValue]);

  return (
    <FormikForm>
      <FilterPanel id='financial-filter'>
        <div className='grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-12'>
          <div className='md:col-span-6'>
            <Field
              id='calendar'
              label='Últimos lançamentos'
              name='dates'
              customPreset={calendarPreset}
              component={DateRangePickerFormField}
              maxDate={new Date()}
              disabled={!isStatementTab}
            />
            {errors.dates && <span className='text-xs text-(--uizz-antd-color-error)'>{errors.dates}</span>}
          </div>

          <div className='md:col-span-3'>
            <Field
              id='movement_type'
              name='movement_type'
              label='Movimentações'
              options={MovementTypesFilter}
              component={SelectFormField}
              disabled={!isStatementTab}
              fullWidth
            />
          </div>

          <div className='md:col-span-3'>
            <Field
              id='transaction_type'
              name='transaction_type'
              label='Tipo da transação'
              options={transactionTypesFilter}
              component={SelectFormField}
              disabled={!isStatementTab}
              fullWidth
            />
          </div>
        </div>

        <div className='mt-3 grid grid-cols-1 items-start gap-x-4 gap-y-3 md:grid-cols-12'>
          <div className='md:col-span-3'>
            <Field
              id='search_type'
              name='search_type'
              label='Busque por'
              options={SearchTypeFilter}
              component={SelectFormField}
              fullWidth
            />
          </div>

          <div className='md:col-span-6'>
            <Field
              id='search_text'
              name='search_text'
              label='Termo de busca'
              placeholder={PlaceholderForSearchType(values.search_type) || 'Digite os dados da sua busca'}
              mask={values.search_type === SearchType.DOCUMENT ? 'document' : null}
              component={TextFormField}
              fullWidth
              disabled={values.search_type === SearchType.ALL}
            />
          </div>

          <div className='md:col-span-3'>
            <div className='flex flex-col'>
              <div className='h-10' aria-hidden />
              <Button
                block
                size='small'
                icon={<Icon.Search size={16} />}
                type='primary'
                disabled={values.search_type === SearchType.ALL || !values.search_text || !!errors.search_text}
                onClick={handleSearch}
              >
                Buscar
              </Button>
            </div>
          </div>
        </div>
      </FilterPanel>
    </FormikForm>
  );
}

export default Form;
